// views/Profile/components/OrgSubscriptionCard.jsx
import React, { useEffect, useRef, useState } from 'react';
import { Crown, Copy, Check, AlertCircle, Sparkles } from 'lucide-react';
import './OrgSubscriptionCard.css';

const formatCoins = (value) => {
  if (value == null || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('en-US').format(value);
};

const formatLongDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatAnchor = (anchorDate) => {
  if (!anchorDate) return null;
  const d = new Date(anchorDate);
  if (Number.isNaN(d.getTime())) return null;
  const day = d.getUTCDate();
  // 11/12/13 → "th"; 1/21/31 → "st"; 2/22 → "nd"; 3/23 → "rd"; rest → "th".
  const tens = day % 100;
  let suffix = 'th';
  if (tens < 11 || tens > 13) {
    const ones = day % 10;
    if (ones === 1) suffix = 'st';
    else if (ones === 2) suffix = 'nd';
    else if (ones === 3) suffix = 'rd';
  }
  return `${day}${suffix} of each month`;
};

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      timerRef.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard may be unavailable in some contexts — silent */
    }
  };

  return (
    <button
      type="button"
      className="org-sub__copy"
      onClick={handleCopy}
      aria-label={`Copy ${value}`}
    >
      {copied ? <Check size={12} aria-hidden="true" /> : <Copy size={12} aria-hidden="true" />}
    </button>
  );
}

/**
 * OrgSubscriptionCard — Enterprise subscription metadata.
 *
 * Surfaces the subscription row that backs the pool: allowance,
 * billing-cycle anchor, last reset, created/updated timestamps, and
 * the subscription id (with a copy button so support handoffs are
 * one click).
 *
 * Subscription `id` is shown but not heavily emphasised — the
 * monetary numbers belong to OrgBalanceCard.
 */
export default function OrgSubscriptionCard({
  subscription,
  loading = false,
  error = null,
}) {
  if (loading) {
    return (
      <section className="org-sub org-sub--loading" aria-busy="true">
        <div className="org-sub__header">
          <span className="org-sub__label">
            <Crown size={16} aria-hidden="true" />
            Subscription
          </span>
        </div>
        <div className="org-sub__skel-row" />
        <div className="org-sub__skel-row" />
        <div className="org-sub__skel-row" />
      </section>
    );
  }

  if (error) {
    return (
      <section className="org-sub org-sub--error">
        <div className="org-sub__header">
          <span className="org-sub__label">
            <Crown size={16} aria-hidden="true" />
            Subscription
          </span>
        </div>
        <div className="org-sub__error">
          <AlertCircle size={20} aria-hidden="true" />
          <p>{error.message || 'Unable to load subscription'}</p>
          {error.requestId && (
            <small className="org-sub__request-id">
              ref: {error.requestId}
            </small>
          )}
        </div>
      </section>
    );
  }

  if (!subscription) {
    return null;
  }

  const anchor = formatAnchor(subscription.billingCycleAnchorDate);
  const lastReset = subscription.lastPeriodResetAt
    ? formatLongDate(subscription.lastPeriodResetAt)
    : null;
  const createdAt = subscription.createdAt
    ? formatLongDate(subscription.createdAt)
    : null;

  return (
    <section className="org-sub">
      <header className="org-sub__header">
        <span className="org-sub__label">
          <Crown size={16} aria-hidden="true" />
          Subscription
        </span>
        {subscription.isEnterprise && (
          <span className="org-sub__tag">
            <Sparkles size={12} aria-hidden="true" />
            Enterprise
          </span>
        )}
      </header>

      <div className="org-sub__hero">
        <span className="org-sub__hero-number">
          {formatCoins(subscription.customMonthlyCoinAllowance)}
        </span>
        <span className="org-sub__hero-suffix">coins / month</span>
      </div>

      <dl className="org-sub__list">
        {anchor && (
          <div className="org-sub__row">
            <dt>Billing anchor</dt>
            <dd>{anchor}</dd>
          </div>
        )}
        {lastReset && (
          <div className="org-sub__row">
            <dt>Last reset</dt>
            <dd>{lastReset}</dd>
          </div>
        )}
        {createdAt && (
          <div className="org-sub__row">
            <dt>Active since</dt>
            <dd>{createdAt}</dd>
          </div>
        )}
        {subscription.id && (
          <div className="org-sub__row org-sub__row--id">
            <dt>Subscription ID</dt>
            <dd>
              <code className="org-sub__id-value">{subscription.id}</code>
              <CopyButton value={String(subscription.id)} />
            </dd>
          </div>
        )}
      </dl>
    </section>
  );
}
